// --- START EDIT: apply catalog validators to normalize gross_price/discount_percent ---
import api from './api';
import axiosInstance from '../auth/axiosWithInterceptor';
import type {
  Product,
  ProductCategory,
  RetailFilamentRoll,
  RetailFilamentBrand,
  RetailFilamentMaterial,
} from '../types/catalog';
import {
  safeParseProducts,
  parseProduct,
  safeParseFilamentRolls,
  parseFilamentRoll,
} from './validators/catalog';

// Public endpoints
export const fetchProducts = (params?: { category_id?: number; search?: string }) =>
  api.get<unknown>('/products', { params }).then(r => safeParseProducts(r.data) as Product[]);

export const fetchProduct = (slug: string) =>
  api.get<unknown>(`/products/${slug}`).then(r => parseProduct(r.data) as Product);

export const fetchCategories = () =>
  api.get<ProductCategory[]>('/catalog/categories').then(r => r.data);

export const fetchFilamentBrands = () =>
  api.get<RetailFilamentBrand[]>('/filaments/brands').then(r => r.data);

export const fetchFilamentMaterials = (brandSlug: string) =>
  api.get<RetailFilamentMaterial[]>(`/filaments/brands/${brandSlug}/materials`).then(r => r.data);

export const fetchFilaments = (params?: {
  brand_id?: number;
  material_id?: number;
  search?: string;
  color_hex?: string;
}) => api.get<unknown>('/filaments', { params }).then(r => safeParseFilamentRolls(r.data) as RetailFilamentRoll[]);

export const fetchFilament = (slug: string) =>
  api.get<unknown>(`/filaments/${slug}`).then(r => parseFilamentRoll(r.data) as RetailFilamentRoll);

// Admin endpoints
export const adminFetchProducts = (opts?: { archived?: boolean }) => {
  const params: Record<string, string> = {};
  if (opts?.archived === true) params.only_archived = "true";
  return axiosInstance
    .get<unknown>('/admin/catalog/products', { params })
    .then(r => safeParseProducts(r.data) as Product[]);
};

// --- START EDIT: next-code suggestion endpoint ---
export const adminGetNextProductCode = (): Promise<{ suggested_code: number }> =>
  axiosInstance.get('/admin/catalog/products/next-code').then(r => r.data);
// --- END EDIT ---

export const adminCreateProduct = (data: unknown) =>
  axiosInstance.post<Product>('/admin/catalog/products', data).then(r => r.data);

export const adminUpdateProduct = (id: number, data: unknown) =>
  axiosInstance.patch<Product>(`/admin/catalog/products/${id}`, data).then(r => r.data);

export const adminDeleteProduct = (id: number) =>
  axiosInstance.delete(`/admin/catalog/products/${id}`).then(r => r.data);

// --- START EDIT: permanent (hard) delete – requires is_archived=True (Policy C) ---
export const adminPermanentDeleteProduct = (id: number) =>
  axiosInstance.delete(`/admin/catalog/products/${id}/permanent`).then(r => r.data);
// --- END EDIT ---

export const adminFetchCategories = () =>
  axiosInstance.get<ProductCategory[]>('/admin/catalog/categories').then(r => r.data);

export const adminCreateCategory = (data: unknown) =>
  axiosInstance.post<ProductCategory>('/admin/catalog/categories', data).then(r => r.data);

export const adminUpdateCategory = (id: number, data: unknown) =>
  axiosInstance.patch<ProductCategory>(`/admin/catalog/categories/${id}`, data).then(r => r.data);

export const adminDeleteCategory = (id: number) =>
  axiosInstance.delete(`/admin/catalog/categories/${id}`).then(r => r.data);

export const adminFetchBrands = () =>
  axiosInstance.get('/admin/catalog/filaments/brands').then(r => r.data);

export const adminCreateBrand = (data: unknown) =>
  axiosInstance.post('/admin/catalog/filaments/brands', data).then(r => r.data);

export const adminUpdateBrand = (id: number, data: unknown) =>
  axiosInstance.patch(`/admin/catalog/filaments/brands/${id}`, data).then(r => r.data);

export const adminDeactivateBrand = (id: number) =>
  axiosInstance.delete(`/admin/catalog/filaments/brands/${id}`).then(r => r.data);

export const adminGetBrandBannerUploadUrl = (
  brandId: number,
  filename: string,
  contentType: string,
) =>
  axiosInstance
    .post(`/admin/catalog/filaments/brands/${brandId}/banner/upload-url`, {
      filename,
      content_type: contentType,
    })
    .then(r => r.data);

export const adminSetBrandBanner = (
  brandId: number,
  data: { r2_key: string; url: string },
) =>
  axiosInstance
    .post(`/admin/catalog/filaments/brands/${brandId}/banner`, data)
    .then(r => r.data);

export const adminDeleteBrandBanner = (brandId: number) =>
  axiosInstance
    .delete(`/admin/catalog/filaments/brands/${brandId}/banner`)
    .then(r => r.data);
// --- END EDIT: brand banner management ---

export const adminFetchMaterials = () =>
  axiosInstance
    .get('/admin/catalog/filaments/materials')
    .then(r => r.data);

export const adminCreateMaterial = (data: unknown) =>
  axiosInstance.post('/admin/catalog/filaments/materials', data).then(r => r.data);

export const adminUpdateMaterial = (id: number, data: unknown) =>
  axiosInstance.patch(`/admin/catalog/filaments/materials/${id}`, data).then(r => r.data);

export const adminDeleteMaterial = (id: number) =>
  axiosInstance.delete(`/admin/catalog/filaments/materials/${id}`).then(r => r.data);

export const adminFetchRolls = (opts?: { archived?: boolean }) => {
  const params: Record<string, string> = {};
  if (opts?.archived === true) params.only_archived = "true";
  return axiosInstance
    .get<unknown>('/admin/catalog/filaments/rolls', { params })
    .then(r => safeParseFilamentRolls(r.data) as RetailFilamentRoll[]);
};

export const adminCreateRoll = (data: unknown) =>
  axiosInstance.post('/admin/catalog/filaments/rolls', data).then(r => r.data);

export const adminUpdateRoll = (id: number, data: unknown) =>
  axiosInstance.patch(`/admin/catalog/filaments/rolls/${id}`, data).then(r => r.data);

export const adminDeleteRoll = (id: number) =>
  axiosInstance.delete(`/admin/catalog/filaments/rolls/${id}`).then(r => r.data);

// --- START EDIT: permanent (hard) delete – requires is_archived=True (Policy C) ---
export const adminPermanentDeleteRoll = (id: number) =>
  axiosInstance.delete(`/admin/catalog/filaments/rolls/${id}/permanent`).then(r => r.data);
// --- END EDIT ---

// Presigned upload URL for product images
export const adminGetProductImageUploadUrl = (
  productId: number,
  filename: string,
  contentType: string,
) =>
  axiosInstance
    .post(`/admin/catalog/products/${productId}/images/upload-url`, {
      filename,
      content_type: contentType,
    })
    .then(r => r.data);

export const adminAddProductImage = (productId: number, data: unknown) =>
  axiosInstance
    .post(`/admin/catalog/products/${productId}/images`, data)
    .then(r => r.data);

export const adminDeleteProductImage = (productId: number, imageId: number) =>
  axiosInstance
    .delete(`/admin/catalog/products/${productId}/images/${imageId}`)
    .then(r => r.data);

export const adminGetRollImageUploadUrl = (
  rollId: number,
  filename: string,
  contentType: string,
) =>
  axiosInstance
    .post(`/admin/catalog/filaments/rolls/${rollId}/images/upload-url`, {
      filename,
      content_type: contentType,
    })
    .then(r => r.data);

export const adminAddRollImage = (rollId: number, data: unknown) =>
  axiosInstance
    .post(`/admin/catalog/filaments/rolls/${rollId}/images`, data)
    .then(r => r.data);

export const adminDeleteRollImage = (rollId: number, imageId: number) =>
  axiosInstance
    .delete(`/admin/catalog/filaments/rolls/${rollId}/images/${imageId}`)
    .then(r => r.data);

// Add to cart
export const addProductToCart = (productId: number, quantity: number) =>
  axiosInstance
    .post('/cart/items', { product_id: productId, quantity }, { withCredentials: true })
    .then(r => r.data);

export const addFilamentToCart = (rollId: number, quantity: number) =>
  axiosInstance
    .post('/cart/items', { retail_filament_roll_id: rollId, quantity }, { withCredentials: true })
    .then(r => r.data);
// --- END EDIT: apply catalog validators to normalize gross_price/discount_percent ---
